import { Link } from 'react-router-dom';

export default function Content() {
  return (
    <article className="max-w-4xl mx-auto px-6 py-16">
      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-[#1a1916]">
          Types of Upright Piano We Move in London
        </h2>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          <strong>Upright piano movers in London</strong> handle a wide variety of upright piano types, each requiring specific expertise. <strong>Spinet uprights</strong> (90cm tall, 90kg) are the smallest and most compact, ideal for small London flats. <strong>Console uprights</strong> (100-110cm, 100-150kg) are popular in modern homes and offer better sound quality than spinets.
        </p>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          <strong>Studio or professional uprights</strong> (110-130cm, 150-250kg) are found in music schools and serious pianists' homes across London. <strong>Full-size overstrung uprights</strong> (130-135cm, 200-300kg) represent the pinnacle of upright piano design, with longer strings and superior tone. Historic <strong>upright grand pianos</strong> (135cm+, 300kg+) are rare Victorian instruments requiring exceptional care during removal.
        </p>
        <p className="text-lg leading-relaxed text-[#2a2826]">
          Each upright piano type demands different handling techniques, equipment, and crew size. Professional <strong>upright piano removal London</strong> specialists assess your specific instrument before providing a fixed-price quote.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-[#1a1916]">
          Upright Piano Removal — Stairs and Difficult Access
        </h2>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          <strong>Upright piano removal in London</strong> frequently involves navigating stairs and challenging access points. Unlike furniture, upright pianos cannot be tilted backward on stairs — doing so causes the internal hammers to fall forward and damage the action mechanism. Professional movers carry uprights in an upright position or use specialist piano boards at angles up to 45 degrees maximum.
        </p>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          London properties present unique challenges for <strong>upright piano movers</strong>. Narrow Victorian terraces with steep staircases, basement flats requiring descent down external steps, top-floor flats in buildings without lifts, and spiral staircases all demand specialist expertise and equipment. Some London properties have doorways narrower than 80cm, requiring door frame removal or alternative access routes.
        </p>
        <p className="text-lg leading-relaxed text-[#2a2826]">
          Experienced <strong>upright piano removal London</strong> companies conduct pre-move surveys to identify access challenges and plan the safest route. In extreme cases, external crane lifts or window removal may be necessary, particularly for full-size overstrung uprights weighing 250-300kg.
        </p>
      </section>

      <div className="my-12 rounded-lg overflow-hidden shadow-lg">
        <img
          src="https://ik.imagekit.io/pianomoverslondon/tr:w-800,h-600,c-at_max,q-85,f-webp/Piano%20Movers%20Gallery%20London%20Jobs/Piano_Movers_London_Pictures_from_job%20(1).jpeg"
          alt="Upright piano removal London — piano being loaded into specialist vehicle"
          className="w-full h-auto"
        />
      </div>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-[#1a1916]">
          Upright Piano Removal Prices London
        </h2>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          <strong>Upright piano removal costs in London</strong> start from £149-£199 for ground floor to ground floor local moves within the same borough. Each flight of stairs adds £50-£80 to the base price, reflecting the additional time, effort, and risk involved in stair navigation.
        </p>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          Cross-London moves (e.g., North London to South London) typically add £50-£100 to the base price due to increased travel time and congestion charge considerations. Weekend moves incur a 15% premium due to higher demand and parking challenges in residential areas.
        </p>
        <p className="text-lg leading-relaxed text-[#2a2826]">
          Always request fixed-price quotes rather than hourly rates for <strong>upright piano removal in London</strong>. For comprehensive pricing information including crane lifts, difficult access surcharges, and hidden costs to avoid, see our detailed <Link to="/piano-removal-cost-london" className="text-[#c9a84c] hover:underline font-semibold">piano removal cost London guide</Link>.
        </p>
      </section>

      <section className="mb-12 bg-white p-8 rounded-lg shadow-md border-l-4 border-[#c9a84c]">
        <h2 className="text-3xl font-bold mb-6 text-[#1a1916]">
          Recommended Upright Piano Removal Service London
        </h2>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          For professional <strong>upright piano removal across London</strong>, we recommend <strong>Piano Service North London</strong> — established specialists with extensive experience moving all types of upright pianos throughout the capital.
        </p>
        <p className="text-lg leading-relaxed mb-4 text-[#2a2826]">
          Their team of <a href="https://www.pianomoveteam.co.uk/piano-removals-london.html" target="_blank" rel="noopener noreferrer" className="text-[#c9a84c] hover:underline font-semibold">piano removals London specialists</a> handles everything from compact spinet uprights to heavy Victorian overstrung instruments. They offer same-day availability, operate 7 days a week, and cover North London, Greater London, and all 32 London boroughs.
        </p>
        <p className="text-lg leading-relaxed text-[#2a2826]">
          Call <strong className="text-[#c9a84c]">020 3441 9463</strong> for a free fixed-price quote. Their upright piano expertise includes specialist equipment for stairs, narrow access, and difficult properties common in London's diverse housing stock.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-3xl font-bold mb-6 text-[#1a1916]">
          How to Prepare Your Upright Piano for Removal in London
        </h2>
        <div className="bg-[#faf9f5] p-6 rounded-lg">
          <ul className="space-y-3 text-lg text-[#2a2826]">
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-[#c9a84c] text-xl mr-3 mt-1"></i>
              <span><strong>Lock the keyboard lid</strong> to prevent it opening during transit and damaging keys</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-[#c9a84c] text-xl mr-3 mt-1"></i>
              <span><strong>Remove all items from the piano top</strong> including photos, ornaments, and sheet music</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-[#c9a84c] text-xl mr-3 mt-1"></i>
              <span><strong>Measure your narrowest doorway</strong> — most uprights need 80cm clear width minimum</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-[#c9a84c] text-xl mr-3 mt-1"></i>
              <span><strong>Inform movers about stairs in advance</strong> including number of flights and any tight turns</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-[#c9a84c] text-xl mr-3 mt-1"></i>
              <span><strong>Check if your piano has working castors</strong> — this makes ground floor moves easier</span>
            </li>
            <li className="flex items-start">
              <i className="ri-checkbox-circle-fill text-[#c9a84c] text-xl mr-3 mt-1"></i>
              <span><strong>Note any stiff or broken keys</strong> that might indicate internal issues requiring extra care</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="mb-12">
        <h3 className="text-2xl font-bold mb-4 text-[#1a1916]">Related Services</h3>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/grand-piano-removal-london"
            className="text-[#c9a84c] hover:underline font-semibold text-lg cursor-pointer"
          >
            Grand Piano Removal London
          </Link>
          <span className="text-[#2a2826]">•</span>
          <Link
            to="/piano-movers-london"
            className="text-[#c9a84c] hover:underline font-semibold text-lg cursor-pointer"
          >
            Piano Movers London
          </Link>
        </div>
      </section>
    </article>
  );
}